import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { fetchAvailability, submitBooking } from "../lib/api";
import ContactBar from "../components/ContactBar";

const MAX_PARTY = 10;

// Local-time ISO date (YYYY-MM-DD). We deliberately do NOT use
// toISOString() because that returns UTC — in AWST (UTC+8) the local
// "today" can map to yesterday or tomorrow in UTC, which breaks the
// "is this slot in the past?" check.
function localIso(d = new Date()) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

// Convert "17:00" / "17:30" → "5pm" / "5:30pm"
export function format12h(t) {
    if (!t || typeof t !== "string" || !t.includes(":")) return t;
    const [hStr, mStr] = t.split(":");
    const h24 = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h24) || Number.isNaN(m)) return t;
    const period = h24 >= 12 ? "pm" : "am";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, "0")}${period}`;
}

// Build a list of selectable dates (next 30 days, skip Sundays).
// If it's already past the last bookable slot of the day (19:30 local),
// today is hidden so the next available date is tomorrow.
function buildDateOptions() {
    const out = [];
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    // After 7:30pm today is no longer bookable — restaurant is essentially
    // done taking reservations for the day. Skip to tomorrow.
    const pastLastSlot =
        now.getHours() > 19 ||
        (now.getHours() === 19 && now.getMinutes() >= 30);
    const startOffset = pastLastSlot ? 1 : 0;

    for (let i = startOffset; i < 30 + startOffset; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        if (d.getDay() === 0) continue; // Sun
        const iso = localIso(d);
        const label = d.toLocaleDateString("en-AU", {
            weekday: "short",
            day: "numeric",
            month: "short",
        });
        out.push({ iso, label });
    }
    return out;
}

export default function BookPage() {
    const dateOptions = useMemo(buildDateOptions, []);
    const [step, setStep] = useState("form"); // form | success
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    const [largeGroup, setLargeGroup] = useState(false);
    // Free-text input while user is typing large group size; parsed/clamped on blur.
    const [largeGroupInput, setLargeGroupInput] = useState("12");

    const [form, setForm] = useState({
        party_size: 2,
        date: dateOptions[0]?.iso || "",
        time: "",
        name: "",
        mobile: "",
        email: "",
        special_requests: "",
    });

    const [availability, setAvailability] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);

    useEffect(() => {
        if (!form.date) return;
        setLoadingSlots(true);
        setAvailability(null);
        fetchAvailability(form.date)
            .then((data) => setAvailability(data))
            .catch(() => setAvailability(null))
            .finally(() => setLoadingSlots(false));
    }, [form.date]);

    const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        if (!form.time) {
            setError("Pick a time first.");
            return;
        }
        if (!form.name.trim()) {
            setError("We need a name for the booking.");
            return;
        }
        if (!form.mobile.trim()) {
            setError(
                "A mobile number — we'll text you to confirm your table."
            );
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                mobile: form.mobile.trim(),
                party_size: Number(form.party_size),
                date: form.date,
                time: form.time,
                ...(form.email.trim() ? { email: form.email.trim() } : {}),
                ...(form.special_requests.trim()
                    ? { special_requests: form.special_requests.trim() }
                    : {}),
            };
            const result = await submitBooking(payload);
            setConfirmation(result);
            setStep("success");
        } catch (err) {
            setError(err.message || "Something went wrong.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <>
            <section
                data-testid="book-page"
                className="relative pt-32 md:pt-36 pb-20 md:pb-28 bg-[#FAF8F4] overflow-hidden"
            >
                <div className="mx-auto max-w-[1200px] px-6 md:px-10 lg:px-14">
                    {/* Heading */}
                    <motion.p
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="overline mb-3 !text-[#121212]"
                    >
                        Book a table
                    </motion.p>
                    <h1 className="font-display uppercase text-[#121212] text-[clamp(2.4rem,6vw,5rem)] leading-[0.93]">
                        Hold us
                        <br />
                        a <span className="text-[#EB4A18]">spot.</span>
                    </h1>
                    <p className="mt-6 max-w-xl text-[#4A4A4A] text-base md:text-lg leading-relaxed">
                        Tables for up to 10. We'll text you to confirm — usually
                        within the hour during opening.
                    </p>

                    {step === "form" && (
                        <form
                            onSubmit={onSubmit}
                            className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10"
                            data-testid="booking-form"
                        >
                            {/* LEFT — slot pickers */}
                            <div className="lg:col-span-7 space-y-10">
                                {/* Party size */}
                                <div>
                                    <FieldLabel n="01" label="Party size" />
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {Array.from({ length: MAX_PARTY }).map(
                                            (_, i) => {
                                                const v = i + 1;
                                                const active =
                                                    !largeGroup &&
                                                    Number(form.party_size) === v;
                                                return (
                                                    <button
                                                        key={v}
                                                        type="button"
                                                        data-testid={`party-${v}`}
                                                        onClick={() => {
                                                            setLargeGroup(false);
                                                            onChange("party_size", v);
                                                        }}
                                                        className={`w-14 h-14 font-display text-2xl border-2 transition-colors duration-200 ${
                                                            active
                                                                ? "bg-[#121212] text-[#FAF8F4] border-[#121212]"
                                                                : "bg-white text-[#121212] border-black/15 hover:border-[#121212]"
                                                        }`}
                                                    >
                                                        {v}
                                                    </button>
                                                );
                                            }
                                        )}
                                        {/* 10+ tile */}
                                        <button
                                            type="button"
                                            data-testid="party-large"
                                            onClick={() => {
                                                setLargeGroup(true);
                                                const seed = Math.max(
                                                    11,
                                                    Number(largeGroupInput) || 12
                                                );
                                                setLargeGroupInput(String(seed));
                                                onChange("party_size", seed);
                                            }}
                                            className={`px-5 h-14 font-display text-2xl border-2 transition-colors duration-200 ${
                                                largeGroup
                                                    ? "bg-[#EB4A18] text-[#FAF8F4] border-[#EB4A18]"
                                                    : "bg-white text-[#121212] border-black/15 hover:border-[#EB4A18]"
                                            }`}
                                        >
                                            10+
                                        </button>
                                    </div>
                                    {largeGroup && (
                                        <div className="mt-4 bg-[#121212] text-[#FAF8F4] p-5 max-w-sm">
                                            <p className="overline !text-[#FAF8F4]/60">
                                                Larger group
                                            </p>
                                            <label
                                                htmlFor="large-size"
                                                className="block mt-2"
                                            >
                                                <span className="text-sm text-[#C7C2B8]">
                                                    How many of you?
                                                </span>
                                                <input
                                                    id="large-size"
                                                    type="number"
                                                    inputMode="numeric"
                                                    min={11}
                                                    max={50}
                                                    data-testid="party-large-input"
                                                    value={largeGroupInput}
                                                    onChange={(e) => {
                                                        // Accept any digits while typing.
                                                        const raw = e.target.value;
                                                        setLargeGroupInput(raw);
                                                        const n = parseInt(raw, 10);
                                                        if (!isNaN(n) && n > 0) {
                                                            onChange(
                                                                "party_size",
                                                                n
                                                            );
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        // Clamp on blur so the
                                                        // visible value stays
                                                        // valid (11–50).
                                                        let n = parseInt(
                                                            largeGroupInput,
                                                            10
                                                        );
                                                        if (isNaN(n)) n = 12;
                                                        if (n < 11) n = 11;
                                                        if (n > 50) n = 50;
                                                        setLargeGroupInput(
                                                            String(n)
                                                        );
                                                        onChange("party_size", n);
                                                    }}
                                                    className="mt-2 w-full bg-transparent border-b-2 border-[#FAF8F4]/30 py-2 text-3xl font-display tracking-tight focus:border-[#EB4A18] outline-none"
                                                />
                                            </label>
                                            <p className="mt-3 text-xs text-[#FAF8F4]/60 leading-relaxed">
                                                We'll text you to confirm we can
                                                fit the whole crew — give us a
                                                little extra notice for big groups.
                                            </p>
                                        </div>
                                    )}
                                    {!largeGroup && (
                                        <p className="mt-3 text-xs text-[#6B6B6B]">
                                            Tables up to 10. Bigger party? Tap{" "}
                                            <span className="font-semibold">
                                                10+
                                            </span>
                                            .
                                        </p>
                                    )}
                                </div>

                                {/* Date */}
                                <div>
                                    <FieldLabel n="02" label="Date" />
                                    <div className="mt-4 flex flex-wrap gap-2 max-h-[200px] overflow-y-auto pr-1">
                                        {dateOptions.map((d) => {
                                            const active = form.date === d.iso;
                                            return (
                                                <button
                                                    key={d.iso}
                                                    type="button"
                                                    data-testid={`date-${d.iso}`}
                                                    onClick={() => {
                                                        // Clear any previously
                                                        // selected time so a
                                                        // stale slot doesn't
                                                        // stay highlighted on
                                                        // the new day.
                                                        onChange("time", "");
                                                        onChange("date", d.iso);
                                                    }}
                                                    className={`px-4 py-3 text-xs uppercase tracking-[0.18em] font-semibold border-2 transition-colors duration-200 ${
                                                        active
                                                            ? "bg-[#121212] text-[#FAF8F4] border-[#121212]"
                                                            : "bg-white text-[#121212] border-black/15 hover:border-[#121212]"
                                                    }`}
                                                >
                                                    {d.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Time */}
                                <div>
                                    <FieldLabel n="03" label="Time" />
                                    {loadingSlots && (
                                        <p className="mt-4 overline text-[#6B6B6B]">
                                            Loading slots…
                                        </p>
                                    )}
                                    {availability?.closed && (
                                        <p className="mt-4 overline text-[#6B6B6B]">
                                            Closed on Sundays — pick another day.
                                        </p>
                                    )}
                                    {availability && !availability.closed && (
                                        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                            {availability.slots.map((s) => {
                                                const fullyBooked = s.available <= 0;
                                                // For today only: slots already in the past
                                                // (or less than 1h away — our lead-time rule)
                                                // get the same struck-through disabled state.
                                                let passed = false;
                                                if (form.date) {
                                                    const todayIso = localIso();
                                                    if (form.date === todayIso) {
                                                        const [hh, mm] = s.time
                                                            .split(":")
                                                            .map(Number);
                                                        const slotMs = new Date();
                                                        slotMs.setHours(hh, mm, 0, 0);
                                                        const leadMs =
                                                            slotMs.getTime() -
                                                            Date.now();
                                                        passed = leadMs < 60 * 60 * 1000;
                                                    }
                                                }
                                                const disabled =
                                                    fullyBooked || passed;
                                                const active =
                                                    form.time === s.time;
                                                return (
                                                    <button
                                                        key={s.time}
                                                        type="button"
                                                        disabled={disabled}
                                                        data-testid={`time-${s.time}`}
                                                        onClick={() =>
                                                            onChange("time", s.time)
                                                        }
                                                        className={`px-3 py-3 text-sm font-display tracking-tight border-2 transition-colors duration-200 ${
                                                            active
                                                                ? "bg-[#121212] text-[#FAF8F4] border-[#121212]"
                                                                : disabled
                                                                ? "bg-white text-[#B0B0B0] border-black/10 line-through cursor-not-allowed"
                                                                : "bg-white text-[#121212] border-black/15 hover:border-[#121212]"
                                                        }`}
                                                    >
                                                        {format12h(s.time)}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT — contact + submit */}
                            <div className="lg:col-span-5">
                                <div className="bg-white border border-black/10 p-6 md:p-8 shadow-[0_22px_44px_-30px_rgba(0,0,0,0.4)]">
                                    <FieldLabel n="04" label="Your details" />
                                    <div className="mt-5 space-y-4">
                                        <Input
                                            id="name"
                                            label="Name"
                                            value={form.name}
                                            onChange={(v) => onChange("name", v)}
                                            required
                                        />
                                        <Input
                                            id="mobile"
                                            type="tel"
                                            label="Mobile"
                                            value={form.mobile}
                                            onChange={(v) =>
                                                onChange("mobile", v)
                                            }
                                            required
                                        />
                                        <Input
                                            id="email"
                                            type="email"
                                            label="Email (optional)"
                                            value={form.email}
                                            onChange={(v) =>
                                                onChange("email", v)
                                            }
                                        />
                                        <TextArea
                                            id="notes"
                                            label="Special requests"
                                            value={form.special_requests}
                                            onChange={(v) =>
                                                onChange(
                                                    "special_requests",
                                                    v
                                                )
                                            }
                                        />
                                    </div>

                                    {error && (
                                        <p
                                            data-testid="booking-error"
                                            className="mt-5 text-sm text-[#EB4A18]"
                                        >
                                            {error}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="booking-submit"
                                        className="btn-primary mt-7 w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {submitting
                                            ? "Sending…"
                                            : "Request Booking"}
                                        {!submitting && (
                                            <span aria-hidden="true">→</span>
                                        )}
                                    </button>
                                    <p className="mt-4 overline text-[0.6rem] text-[#6B6B6B] tracking-[0.28em]">
                                        We'll text you to confirm.
                                    </p>
                                </div>
                            </div>
                        </form>
                    )}

                    {step === "success" && confirmation && (
                        <BookingSuccess booking={confirmation} />
                    )}
                </div>
            </section>

            <ContactBar />
        </>
    );
}

function FieldLabel({ n, label }) {
    return (
        <div className="flex items-baseline gap-3">
            <span className="font-display text-2xl text-[#EB4A18]">{n}</span>
            <span className="font-display uppercase text-xl md:text-2xl text-[#121212] tracking-tight">
                {label}
            </span>
        </div>
    );
}

function Input({ id, label, value, onChange, type = "text", required }) {
    return (
        <label htmlFor={id} className="block">
            <span className="overline text-[#6B6B6B]">
                {label}
                {required && <span className="text-[#EB4A18]"> *</span>}
            </span>
            <input
                id={id}
                data-testid={`booking-input-${id}`}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                autoComplete={
                    id === "mobile"
                        ? "tel"
                        : id === "email"
                        ? "email"
                        : id === "name"
                        ? "name"
                        : "off"
                }
                className="mt-1 w-full bg-[#FAF8F4] border-2 border-black/15 px-4 py-3 text-[#121212] focus:border-[#121212] outline-none transition-colors"
            />
        </label>
    );
}

function TextArea({ id, label, value, onChange }) {
    return (
        <label htmlFor={id} className="block">
            <span className="overline text-[#6B6B6B]">{label}</span>
            <textarea
                id={id}
                data-testid={`booking-input-${id}`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={3}
                placeholder="Highchair? Birthday? Anything else."
                className="mt-1 w-full bg-[#FAF8F4] border-2 border-black/15 px-4 py-3 text-[#121212] focus:border-[#121212] outline-none transition-colors resize-none"
            />
        </label>
    );
}

function BookingSuccess({ booking }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
            data-testid="booking-success"
            className="mt-12 bg-[#121212] text-[#FAF8F4] p-10 md:p-14 max-w-3xl"
        >
            <p className="overline mb-4 !text-[#FAF8F4]/60">
                Booking received
            </p>
            <h2 className="font-display uppercase text-[clamp(2rem,5vw,3.5rem)] leading-[0.95]">
                We've got{" "}
                <span className="text-[#EB4A18]">your request.</span>
            </h2>
            <dl className="mt-8 grid grid-cols-2 gap-y-4 gap-x-8 max-w-md">
                <dt className="overline text-[#FAF8F4]/60">Name</dt>
                <dd className="font-display text-2xl">{booking.name}</dd>
                <dt className="overline text-[#FAF8F4]/60">Party</dt>
                <dd className="font-display text-2xl">{booking.party_size}</dd>
                <dt className="overline text-[#FAF8F4]/60">Date</dt>
                <dd className="font-display text-2xl">{booking.date}</dd>
                <dt className="overline text-[#FAF8F4]/60">Time</dt>
                <dd className="font-display text-2xl">{format12h(booking.time)}</dd>
            </dl>
            <p className="mt-8 text-[#C7C2B8] leading-relaxed max-w-lg">
                We'll text you on{" "}
                <span className="text-[#FAF8F4]">{booking.mobile}</span> to
                confirm. Look out for a message from The Gaffer.
            </p>
        </motion.div>
    );
}
